/**
 * Persisted datastore blob for Vuex (`mapToDatastore` / `mapFromDatastore`).
 * Single outer version only (`schemeVersion`); see docs/CHANGELOG-lab-persistence.md.
 */

/** Current persisted JSON shape (bump and document when fields change). */
export const SCHEME_VERSION = 1;

/**
 * Coerce one attempt for storage / load. Does not attach per-attempt schema versions (YAGNI).
 *
 * @param {unknown} attempt
 * @returns {object}
 */
export function normalizeLabAttempt(attempt) {
  if (!attempt || typeof attempt !== "object") {
    throw new Error("[labAttemptPersistence] Invalid attempt: expected object");
  }

  // Used to type-check the attempt object
  const a = /** @type {Record<string, unknown>} */ (attempt);

  if (typeof a.attemptId !== "string" || !a.attemptId) {
    throw new Error(
      "[labAttemptPersistence] Invalid attempt: missing attemptId"
    );
  }

  const isComplete = Boolean(a.isComplete);

  const timestamp =
    typeof a.timestamp === "string" ? a.timestamp : new Date().toISOString();

  const completedAt =
    a.completedAt == null || a.completedAt === ""
      ? null
      : typeof a.completedAt === "string"
        ? a.completedAt
        : new Date().toISOString();

  const measurements =
    a.measurements && Array.isArray(a.measurements) ? [...a.measurements] : [];

  const formData =
    a.formData && typeof a.formData === "object" && !Array.isArray(a.formData)
      ? { ...a.formData }
      : {};

  const solutions =
    a.solutions &&
    typeof a.solutions === "object" &&
    !Array.isArray(a.solutions)
      ? { ...a.solutions }
      : {};

  const normalized = {
    attemptId: a.attemptId,
    timestamp,
    isComplete,
    completedAt,
    measurements,
    formData,
    solutions
  };

  if (Array.isArray(a.actionLogs)) {
    normalized.actionLogs = a.actionLogs.map((x) =>
      x && typeof x === "object" ? { ...x } : x
    );
  }

  return normalized;
}

/**
 * @param {object} parsed
 */
function normalizeHydratedDatastore(parsed) {
  if (parsed.schemeVersion !== SCHEME_VERSION) {
    if (import.meta.env.DEV) {
      console.warn(
        `[labAttemptPersistence] Expected schemeVersion ${SCHEME_VERSION}, got ${String(parsed.schemeVersion)}; ignoring stored payload.`
      );
    }
    return emptyNormalizedPayload();
  }

  const rawAttempts = Array.isArray(parsed.labAttempts)
    ? parsed.labAttempts
    : [];
  const labAttempts = [];

  for (let i = 0; i < rawAttempts.length; i++) {
    try {
      labAttempts.push(normalizeLabAttempt(rawAttempts[i]));
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn(
          `[labAttemptPersistence] Dropping invalid lab attempt at index ${i}:`,
          err
        );
      }
    }
  }

  return {
    labAttempts,
    currentAttemptId:
      parsed.currentAttemptId == null ? null : String(parsed.currentAttemptId),
    context:
      parsed.context === undefined
        ? null
        : /** @type {null | object} */ (parsed.context),
    showSimpleUI: Boolean(parsed.showSimpleUI)
  };
}

function emptyNormalizedPayload() {
  return {
    labAttempts: [],
    currentAttemptId: null,
    context: null,
    showSimpleUI: false
  };
}

/**
 * Parse datastore JSON for Vuex. Requires `schemeVersion` to match {@link SCHEME_VERSION}.
 *
 * @param {unknown} input - JSON string from datastore, or already-parsed object
 * @returns {null | { labAttempts: object[], currentAttemptId: string | null, context: object | null, showSimpleUI: boolean }} Null when the payload string is not valid JSON.
 */
export function normalizeDatastorePayloadFromRaw(input) {
  if (input == null || input === "") {
    return emptyNormalizedPayload();
  }

  let parsed;
  if (typeof input === "string") {
    try {
      parsed = JSON.parse(input);
    } catch {
      return null;
    }
  } else if (typeof input === "object") {
    parsed = input;
  } else {
    return emptyNormalizedPayload();
  }

  if (!parsed || typeof parsed !== "object") {
    return emptyNormalizedPayload();
  }

  return normalizeHydratedDatastore(parsed);
}

/**
 * @param {{ labAttempts: object[], currentAttemptId: string | null, context: object | null, showSimpleUI: boolean }} stateSlice
 * @returns {string}
 */
export function buildDatastorePayload(stateSlice) {
  const attempts = [];
  for (const raw of stateSlice.labAttempts || []) {
    try {
      attempts.push(normalizeLabAttempt(raw));
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn(
          "[labAttemptPersistence] Skipping invalid attempt on save:",
          err
        );
      }
    }
  }

  return JSON.stringify({
    schemeVersion: SCHEME_VERSION,
    labAttempts: attempts,
    currentAttemptId: stateSlice.currentAttemptId,
    context: stateSlice.context,
    showSimpleUI: stateSlice.showSimpleUI
  });
}
